import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { assertUnreachable } from '../../utility/general-utility.js';
import { MapUtils } from '../../utility/map-utils.js';
import { isNullOrUndefined } from '../../utility/null-safety.js';
import { VersionRange } from '../core/version-range.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { Price } from './price.js';

export enum FeeType {
  Fixed = 'Fixed',
  Percentage = 'Percentage',
  FixedAndPercentage = 'FixedAndPercentage',
}

export class Fee extends DataModel {
  static readonly TYPE_ID: string = '64f7e0dbc86444a4ac1c236719059af1';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly type: FeeType,
    private readonly _fixedPrice: Price | null,

    // Percentage is stored as a double from 0 to 100, e.g. 10.5 for 10.5%.
    private readonly _percentage: number | null,
  ) {
    super();
  }

  get $typeId(): string {
    return Fee.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, Fee.SCHEMA_VERSION);
  }

  get fixedPrice(): Price {
    if(isNullOrUndefined(this._fixedPrice)) {
      throw new Error(`Cannot get fixed price of application fee if type is: ${this.type}`);
    }

    return this._fixedPrice;
  }

  get fixedPriceOrNull(): Price | null {
    return this._fixedPrice;
  }

  // Percentage is stored as a double from 0 to 100, e.g. 10.5 for 10.5%.
  get percentage(): number {
    if(isNullOrUndefined(this._percentage)) {
      throw new Error(`Cannot get percentage of application fee if type is: ${this.type}`);
    }

    return this._percentage;
  }

  get percentageOrNull(): number | null {
    return this._percentage;
  }

  static fixed(price: Price): Fee {
    return new Fee(FeeType.Fixed, price, null);
  }

  // Percentage is stored as a double from 0 to 100, e.g. 10.5 for 10.5%.
  static percentage(percentage: number): Fee {
    return new Fee(FeeType.Percentage, null, percentage);
  }

  // Percentage is stored as a double from 0 to 100, e.g. 10.5 for 10.5%.
  static both(price: Price, percentage: number): Fee {
    return new Fee(FeeType.FixedAndPercentage, price, percentage);
  }

  static fromMap(context: SerializationContext, map: SerializedData): Fee {
    return new Fee(
        toEnumFromString(MapUtils.get<string>(map, 'type'), FeeType),
        Price.decodeOrNull(MapUtils.getOrNull<string>(map, 'fixedPrice')),
        MapUtils.getOrNull<number>(map, 'percentage'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'type': fromEnumAsString(this.type),
      'fixedPrice': this._fixedPrice?.encode() ?? null,
      'percentage': this._percentage,
    };
  }

  clone(): this {
    return new Fee(
        this.type,
        this._fixedPrice?.clone() ?? null,
        this._percentage,
    ) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.type === this.type &&
      other._percentage === this._percentage &&
      isSameData(other._fixedPrice, this._fixedPrice);
  }

  calculateUnitFee(unitPrice: Price): Price {
    if(this.type === FeeType.Fixed) {
      return this.fixedPrice.currency === unitPrice.currency ? this.fixedPrice : Price.zero(unitPrice.currency);
    }
    else if(this.type === FeeType.Percentage) {
      return unitPrice.scale(this.percentage / 100.0);
    }
    else if(this.type === FeeType.FixedAndPercentage) {
      const fixedFee = this.fixedPrice.currency === unitPrice.currency ? this.fixedPrice: Price.zero(unitPrice.currency);
      const dynamicFee = unitPrice.scale(this.percentage / 100.0);

      return fixedFee.add(dynamicFee);
    }
    else {
      assertUnreachable(this.type);
    }
  }

  toString(): string {
    switch(this.type) {
      case FeeType.Fixed:
        return this.fixedPrice.format();
      case FeeType.Percentage:
        return `${this.percentage}%`;
      case FeeType.FixedAndPercentage:
        return `${this.fixedPrice.format()} + ${this.percentage}%`;
      default:
        assertUnreachable(this.type);
    }
  }
}

export class FeeBuilder extends DataModelBuilder<Fee> {
  build(context: SerializationContext, map: SerializedData): Fee {
    return Fee.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(Fee.SCHEMA_VERSION)) {
      return map;
    }

    if(versionRange.isGreaterThan(Fee.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version we added the "fixed + percentage" fee type
    if(version < 2) {
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
